import { TabFallback } from '@/components/TabFallback';
import type { Permission } from '@cotality/marketplace-contracts/permissions';
import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import type { RouteHandle } from './types';

// `AdminDashboardPage` keeps owning its internal tab routing until G4-T18
// converts each tab into its own nested route. At that point this file
// becomes a parent + children definition rather than a single splat target.
const AdminDashboardPage = lazy(() =>
  import('@/pages/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
);

/**
 * Union of every permission that opens at least one admin surface today.
 * Single source of truth for both the route handle (gates `/admin/*`) and
 * the sidebar's Admin entry (`nav-config.ts` imports this). G4-T18 splits
 * this down to per-tab `requiredPermissions` once tabs are nested routes.
 *
 * `plugins:view` and `teams:view` are intentionally excluded because they
 * are granted to every role per Appendix A; surfacing the admin link to a
 * plain viewer would be misleading.
 */
export const ADMIN_PERMISSIONS = [
  'users:view',
  'users:update_roles',
  'teams:create',
  'teams:update',
  'teams:delete',
  'plugins:approve',
  'plugins:reject',
  'plugins:deprecate',
  'plugins:yank',
  'plugins:submit_requeue',
  'submissions:view',
  'flags:view',
  'audit:view',
] as const satisfies readonly Permission[];

const handle: RouteHandle = {
  crumb: 'Admin',
  requiredPermissions: ADMIN_PERMISSIONS,
};

export const adminRoutes: RouteObject[] = [
  {
    path: 'admin/*',
    element: (
      <Suspense fallback={<TabFallback />}>
        <AdminDashboardPage />
      </Suspense>
    ),
    handle,
  },
];
